import type { Camera } from './footage';
import type { Moment } from './moments';
import type { EventInfo } from './reasons';

const KMH_PER_MPS = 3.6;
const STANDARD_GRAVITY = 9.80665;
const LOCALE = 'en-GB';

export const CAMERA_LABELS: Readonly<Record<Camera, string>> = {
  front: 'Front',
  back: 'Rear',
  left_repeater: 'Left',
  right_repeater: 'Right',
  left_pillar: 'Left pillar',
  right_pillar: 'Right pillar',
};

const dateFormat = new Intl.DateTimeFormat(LOCALE, { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
const shortDateFormat = new Intl.DateTimeFormat(LOCALE, { timeZone: 'UTC', day: 'numeric', month: 'short' });
const timeFormat = new Intl.DateTimeFormat(LOCALE, { timeZone: 'UTC', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' });

export const kmh = (mps: number): number => Math.round(mps * KMH_PER_MPS);

export const formatG = (accelX: number, accelY: number): string => `${(Math.hypot(accelX, accelY) / STANDARD_GRAVITY).toFixed(2)} g`;

export const toG = (mps2: number): number => mps2 / STANDARD_GRAVITY;

export function formatDate(wallMs: number): string {
  const parts = Object.fromEntries(dateFormat.formatToParts(wallMs).map((part) => [part.type, part.value]));
  return `${parts['weekday']} ${parts['day']} ${parts['month']} ${parts['year']}`;
}

export const formatShortDate = (wallMs: number): string => shortDateFormat.format(wallMs);

export const formatTime = (wallMs: number): string => timeFormat.format(wallMs);

export function formatClock(wallMs: number): string {
  const tenths = Math.floor((((wallMs % 1000) + 1000) % 1000) / 100);
  return `${formatTime(wallMs)}.${tenths}`;
}

export function formatDuration(seconds: number): string {
  const whole = Math.max(0, Math.round(seconds));
  if (whole < 60) return `${whole} s`;
  const minutes = Math.floor(whole / 60);
  return `${minutes}:${String(whole % 60).padStart(2, '0')} min`;
}

export const placeLabel = (info: EventInfo | undefined): string =>
  [info?.city, info?.street].filter((part) => part).join(' · ') || 'Unknown place';

export const mapLink = (latitude: number, longitude: number): string =>
  `https://www.openstreetmap.org/?mlat=${latitude.toFixed(5)}&mlon=${longitude.toFixed(5)}#map=18/${latitude.toFixed(5)}/${longitude.toFixed(5)}`;

const MOMENT_LABELS: Readonly<Record<Moment['kind'], (peak: number) => string>> = {
  brake: (peak) => `Hard braking ${peak.toFixed(1)} m/s²`,
  launch: (peak) => `Hard acceleration ${peak.toFixed(1)} m/s²`,
  lateral: (peak) => `Swerve ${peak.toFixed(1)} m/s²`,
  disengage: () => 'Autopilot off',
};

export const momentLabel = (moment: Moment): string => MOMENT_LABELS[moment.kind](moment.peak);

const CLOCK_TEXT = /^(\d{1,2}):(\d{2})(?::(\d{2})(?:\.(\d))?)?$/;
const DAY_MS = 86_400_000;

export function parseClock(text: string, referenceWallMs: number): number | undefined {
  const match = CLOCK_TEXT.exec(text.trim());
  if (!match) return undefined;
  const [hours, minutes, seconds, tenths] = match.slice(1).map((part) => Number(part ?? 0)) as [number, number, number, number];
  if (hours > 23 || minutes > 59 || seconds > 59) return undefined;
  const sameDay = Math.floor(referenceWallMs / DAY_MS) * DAY_MS + ((hours * 60 + minutes) * 60 + seconds) * 1000 + tenths * 100;
  return [sameDay - DAY_MS, sameDay, sameDay + DAY_MS].reduce((best, candidate) =>
    Math.abs(candidate - referenceWallMs) < Math.abs(best - referenceWallMs) ? candidate : best,
  );
}

const BYTE_UNITS = ['bytes', 'KB', 'MB', 'GB', 'TB'] as const;
const KILO = 1024;

export function formatBytes(bytes: number): string {
  const exponent = Math.min(BYTE_UNITS.length - 1, Math.max(0, Math.floor(Math.log(Math.max(1, bytes)) / Math.log(KILO))));
  const value = bytes / KILO ** exponent;
  return `${exponent === 0 ? value : value.toFixed(value < 10 ? 1 : 0)} ${BYTE_UNITS[exponent]}`;
}
